import type { CityConfig } from "../city-template/types";

export const arvadaConfig: CityConfig = {
  city: "Arvada",
  state: "Colorado",
  stateAbbr: "CO",
  slug: "arvada",
  route: "/cities/arvada",
  title: "Emergency & Fast Passport Renewal Arvada CO | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Arvada, CO — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Jefferson County and northwest Denver metro travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Arvada, CO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Arvada and Jefferson County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Arvada%20Colorado%20scenic%20view%20with%20Olde%20Town%20Arvada%20historic%20district%2C%20Jefferson%20County%20foothills%20neighborhood%2C%20warm%20golden%20hour%20light%2C%20tree-lined%20streets%20with%20Rocky%20Mountain%20foothills%20in%20the%20background%2C%20charming%20historic%20downtown%20with%20mountains%20visible%2C%20professional%20cityscape%20photography%2C%20warm%20amber%20and%20golden%20tones%2C%20no%20visible%20text%20or%20people%2C%20cinematic%20wide%20angle%20view&width=1800&height=700&seq=arvada-hero-001&orientation=landscape",
  heroImageAlt: "Arvada Colorado with Olde Town historic district and Rocky Mountain foothills in the background",
  geoPosition: "39.8028;-105.0875",
  geoPlacename: "Arvada, Colorado",
  lastModified: "2026-06-03",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S. Vaughn Way, Aurora, CO 80014",
    distance: "Approximately 20 miles southeast — about 30 minutes via I-70",
  },
  nearbyCitiesZips: [
    { name: "Olde Town Arvada", zips: "80002" },
    { name: "West Arvada", zips: "80003, 80004" },
    { name: "North Arvada", zips: "80003" },
    { name: "South Arvada", zips: "80005" },
    { name: "Wheat Ridge", zips: "80033, 80034" },
    { name: "Westminster", zips: "80030, 80031, 80035" },
    { name: "Broomfield", zips: "80021, 80023" },
    { name: "Golden", zips: "80401, 80403" },
    { name: "Lakewood", zips: "80214, 80215, 80226" },
    { name: "Edgewater", zips: "80214" },
    { name: "Applewood", zips: "80215" },
    { name: "Standley Lake", zips: "80021" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Arvada departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Denver", route: "/cities/denver" },
    { name: "Emergency passport help in Boulder", route: "/cities/boulder" },
    { name: "Urgent passport services in Colorado Springs", route: "/cities/colorado-springs" },
    { name: "Same-day passport help in Wheat Ridge", route: "/cities/arvada" },
    { name: "Expedited passport services in Westminster", route: "/cities/arvada" },
    { name: "Passport courier services in Golden", route: "/cities/arvada" },
    { name: "Rush passport help in Lakewood", route: "/cities/arvada" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Arvada remotely via secure courier. We do not have a walk-in office in Arvada — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Arvada and Jefferson County travelers.",
  fpcOfficeAddress: "3151 S. Vaughn Way, Aurora, CO 80014",
  fpcOfficeDistance: "Serving all of Arvada, Jefferson County, and the northwest Denver metro",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Arvada and the northwest Denver metro. From Olde Town Arvada to the Standley Lake communities, from the Jefferson County foothills to the suburban neighborhoods — we help Arvada travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Arvada neighborhoods and nearby communities including Wheat Ridge, Westminster, Broomfield, Golden, and Lakewood. If you are in Jefferson County or the northwest Denver metro, we have you covered.",
  howItWorksIntro: "Most Arvada travelers complete everything online and ship documents overnight. No need to drive to Aurora or wait for a passport agency appointment. Our emergency and expedited passport process is designed for Arvada travelers who need fast, reliable results.",
  mistakesIntro: "Arvada travelers heading to the mountains for skiing, Europe for vacation, or Mexico for family visits can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Arvada travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Arvada? Here are the most common ones we hear from Jefferson County travelers.",
  statePageRoute: "/states/colorado",
  statePageName: "Colorado",
  dmvWebsite: "https://dmv.colorado.gov",
  dmvLabel: "Colorado DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Arvada international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Lisa R.",
      location: "Olde Town Arvada, CO",
      rating: 5,
      text: "Booked a last-minute Europe trip and my passport was expired. Fast Passport Center had a renewal back to me in 4 days. Highly recommend to anyone in Arvada who needs a fast passport.",
      service: "Emergency Renewal",
    },
    {
      name: "Mike T.",
      location: "West Arvada, CO",
      rating: 5,
      text: "My daughter needed a passport for a school trip to Costa Rica and we waited too long. Fast Passport Center guided us through the child passport process and had it done in 5 days.",
      service: "Child Passport",
    },
    {
      name: "Patricia W.",
      location: "Westminster, CO",
      rating: 5,
      text: "Lost my passport before a cruise to Mexico. They guided me through the replacement process and had it done in 48 hours. No agency appointment needed.",
      service: "Lost Passport",
    },
    {
      name: "David H.",
      location: "Applewood, CO",
      rating: 5,
      text: "I needed to renew for a business trip to Canada and couldn't wait months for standard processing. Fast Passport Center delivered in 5 days. Professional and worth every penny.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "arv-1",
      name: "Arvada Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7350 Ralston Rd",
      city: "Arvada",
      state: "CO",
      zip: "80002",
      phone: "(303) 422-2161",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from Olde Town Arvada",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "arv-2",
      name: "Arvada North Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8200 W. 80th Ave",
      city: "Arvada",
      state: "CO",
      zip: "80005",
      phone: "(303) 421-9650",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Standley Lake area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "North Arvada location for Standley Lake area residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "arv-3",
      name: "Westminster Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6333 W. 120th Ave",
      city: "Westminster",
      state: "CO",
      zip: "80020",
      phone: "(303) 410-0620",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles from central Arvada",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for north Arvada and Westminster residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "arv-4",
      name: "Wheat Ridge Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4200 Yarrow St",
      city: "Wheat Ridge",
      state: "CO",
      zip: "80033",
      phone: "(303) 422-3855",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from Arvada border",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "arv-5",
      name: "Golden Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "520 12th St",
      city: "Golden",
      state: "CO",
      zip: "80401",
      phone: "(303) 279-5503",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles west of Arvada",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for west Arvada and Golden residents. Limited slots available.",
      icon: "ri-mail-line",
    },
    {
      id: "arv-6",
      name: "Jefferson County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "100 Jefferson County Pkwy",
      city: "Golden",
      state: "CO",
      zip: "80419",
      phone: "(303) 271-8168",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles from central Arvada",
      website: "https://www.jeffco.us/",
      websiteLabel: "Jefferson County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Aurora (20 mi)", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Arvada photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Arvada acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Arvada applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Arvada families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Arvada travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Arvada." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Arvada address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Arvada?",
      a: "Fast Passport Center offers emergency passport processing in Arvada in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. This is the fastest reliable option for Arvada travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Arvada?",
      a: "Same-day passports are not available in Arvada. The closest regional passport agency is in Aurora (20 miles). For urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Arvada residents without making the drive.",
    },
    {
      q: "Where can I get a fast passport renewal in Arvada?",
      a: "Fast Passport Center offers fast passport renewal in Arvada with no office visit required. We serve all Arvada neighborhoods including Olde Town, West Arvada, and Standley Lake. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Arvada?",
      a: "The fastest way to get an urgent passport in Arvada is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround.",
    },
    {
      q: "What if I live nearby in Wheat Ridge, Westminster, or Golden?",
      a: "We serve all of Jefferson County and surrounding communities including Wheat Ridge, Westminster, Broomfield, Golden, and Lakewood. Our overnight shipping labels work from any UPS location.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Arvada?",
      a: "Popular options include Arvada Main Post Office (80002), Arvada North Post Office (80005), Wheat Ridge Post Office (80033), and Jefferson County Clerk (80419). All require appointments.",
    },
    {
      q: "How much does expedited passport service cost in Arvada?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote.",
    },
    {
      q: "Do you help with passport photos in Arvada?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Arvada offer passport photo services.",
    },
    {
      q: "What documents do I need for a first-time passport in Arvada?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents.",
    },
    {
      q: "Can you help with a child's passport in Arvada?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Arvada families through the entire process.",
    },
    {
      q: "Do I need to drive to Denver to get an expedited passport from Arvada?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight from Arvada. No need to make the drive into Denver or fight for a Regional Agency appointment.",
    },
    {
      q: "Is Fast Passport Center cheaper than going to the passport agency?",
      a: "In most cases, yes — especially when factoring in the time and difficulty of securing a Regional Agency appointment. Our best price guarantee means we're typically 30–100% less expensive than FedEx, Staples, and other third-party resellers.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Olde Town Arvada", address: "7402 Ralston Rd, Arvada, CO 80002", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — W. 64th Ave", address: "6395 W. 64th Ave, Arvada, CO 80003", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Arvada", address: "7208 W. 88th Ave, Arvada, CO 80005", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Westminster", address: "9701 Westminster Blvd, Westminster, CO 80031", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Arvada", address: "5460 Wadsworth Blvd, Arvada, CO 80002", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Wheat Ridge", address: "3900 Kipling St, Wheat Ridge, CO 80033", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Golden", address: "1400 Washington Ave, Golden, CO 80401", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "Costco — Westminster", address: "14900 W. 88th Pkwy, Arvada, CO 80005", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Arvada",
      address: "11860 W. 52nd Ave, Arvada, CO 80002",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Westminster",
      address: "8450 N. Federal Blvd, Westminster, CO 80031",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center. Extended hours available.",
      featured: false,
    },
    {
      name: "DMV — Wheat Ridge",
      address: "4455 Wadsworth Blvd, Wheat Ridge, CO 80033",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Golden (Jefferson County)",
      address: "100 Jefferson County Pkwy, Golden, CO 80419",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Jefferson County main office. Book online in advance.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "DEN",
      name: "Denver International Airport",
      distance: "35 miles from Arvada via I-70",
      address: "8500 Peña Blvd, Denver, CO 80249",
      note: "The primary international airport for Arvada and Jefferson County travelers. Easy I-70 access from Arvada.",
      flightUrl: "https://www.flydenver.com/",
      flightLabel: "DEN Flight Info",
      featured: true,
    },
    {
      code: "COS",
      name: "Colorado Springs Airport",
      distance: "90 miles from Arvada via I-25",
      address: "7770 Milton E. Proby Pkwy, Colorado Springs, CO 80916",
      note: "Regional airport alternative for southern Colorado connections.",
      flightUrl: "https://www.coloradosprings.gov/airport",
      flightLabel: "Colorado Springs Airport",
      featured: false,
    },
  ],
};